import React from 'react';
import { useLocation } from 'react-router-dom';
import { MONTHS_LIST, getFinancialYearFromDate, parseDateOnly } from 'utils/Dates';
import { routePaths } from '../Routes/data';
import { HeaderContent, FormProps, getDefaultFormProps, bonusStatus, bonusSource, ValidationStates } from './types';
import { BonusDataArraySchemaContract } from 'schemas/BonusDataArraySchema';
import { BonusRow } from './hooks/types';
import {
  formatPayrollMonth,
  getClawbackWindowEndMonth,
  parsePayrollMonth,
  toPayrollMonth,
} from './components/AllocateBonusModal/utils';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { getNumberFromIndianCurrencyString, toIndianCurrencyString } from 'utils/Numbers';
import { differenceInCalendarMonths } from 'date-fns';
import { BadgeProps, CalendarIcon, EditComposeIcon, FilePlusIcon } from '@razorpay/blade/components';


export const getClawbackMonthString = (date: string, clawbackPeriod: number | null) => {

    let payoutDate = parseDateOnly(date);
    let newMonth: number = parseInt(payoutDate.getMonth().toString()) + parseInt(clawbackPeriod ? clawbackPeriod.toString() : '0');
    let newDate = new Date(payoutDate.getFullYear(), newMonth, 1);

    return `${MONTHS_LIST[newDate.getMonth()]}, ${newDate.getFullYear()}`;
}

export const getClawbackMonthStringFromDate = (date: string) => {

    let clawbackMonth = parseDateOnly(date);
    return `${MONTHS_LIST[clawbackMonth.getMonth()]}, ${clawbackMonth.getFullYear()}`;
}

export const ValidatePage = () => {

    const pathname = useLocation().pathname;
    const queryParam = new URLSearchParams(useLocation().search);
    const userId = queryParam.get('userId');
    const bonusId = queryParam.get('id');

    switch (pathname) {
        case routePaths.bonus.create:
        case routePaths.bonus.view:

            if (!userId) {
                return false;
            }
            return true;

        case routePaths.bonus.edit:

            if (!userId && !bonusId) {
                return false;
            }
            return true;
    }

}

export const headerContent = (pathname: string, stepCount: number): HeaderContent => {
    switch (stepCount) {
        case 1:
            return {
                title: pathname === routePaths.bonus.edit ? 'Manage Bonus' : 'Create New Bonus',
                description: pathname === routePaths.bonus.create ? ['Create a bonus and add a clawback period'] : ['Edit the bonus created for your employee'],
                progress: ['IN_PROGRESS', 'INCOMPLETE'],
                titleIcon: pathname === routePaths.bonus.edit ? <EditComposeIcon size={'2xlarge'} marginTop={'spacing.2'} marginRight={'spacing.1'} color="feedback.icon.notice.intense" /> : <FilePlusIcon size={'2xlarge'} marginTop={'spacing.2'} marginRight={'spacing.1'} color="feedback.icon.notice.intense" />
            };
        case 2:
            return {
                title: pathname === routePaths.bonus.edit ? 'Update Clawback Rules' : 'Add Clawback Rules',
                description: [
                    'Add a clawback rule if you want to recover the paid bonus if the employee leaves before a certain period.',
                    'The paid bonus will be recovered during full and final settlement'
                ],
                progress: ['COMPLETED', 'IN_PROGRESS'],
                titleIcon: <CalendarIcon size={'2xlarge'} marginTop={'spacing.2'} marginRight={'spacing.1'} color="feedback.icon.positive.intense" />
            };
        default:
            return {
                title: pathname === routePaths.bonus.edit ? 'Manage Bonus' : 'Create New Bonus',
                description: pathname === routePaths.bonus.create ? ['Create, manage and disburse bonuses to your employees'] : ['Edit the bonus created for your employee'],
                progress: ['IN_PROGRESS', 'INCOMPLETE'],
                titleIcon: pathname === routePaths.bonus.edit ? <EditComposeIcon size={'2xlarge'} marginTop={'spacing.2'} marginRight={'spacing.1'} color="feedback.icon.notice.intense" /> : <FilePlusIcon size={'2xlarge'} marginTop={'spacing.2'} marginRight={'spacing.1'} color="feedback.icon.notice.intense" />
            };
    }
};

export const updateFormProps = (dataArray?: BonusDataArraySchemaContract): FormProps => {

    const data = dataArray?.data[0];

    if (data) {
        return {
            organization_bonus_setting_id: data.organization_bonus_setting_id,
            amount: data.amount,
            source: data.source,
            payout_month: data.payout_month,
            tds_recovery_option: data.tds_recovery_option,
            part_of_ctc: data.part_of_ctc,
            clawback_applicable: data.clawback_applicable,
            clawback_period: data.clawback_period,
            remarks: data.remarks ?? '',
        };
    }
    else {
        return getDefaultFormProps();
    }
}

export const useShouldFetchUserData = (userId: string | null): boolean => {
    const loggedInPeopleId = useSelector(loggedInUserSelectors.peopleId);

    return userId ? parseInt(userId) !== loggedInPeopleId : false;
};

export const handleChange = (e: { name?: string; value?: string | number }, setFormProps: (value: React.SetStateAction<FormProps>) => void) => {
    const { name, value } = e;
    setFormProps((prev) => ({ ...prev, [name || '']: value }));
};

export const handleAmountChange = (amount: string, setFormProps: (value: React.SetStateAction<FormProps>) => void) => {
    setFormProps((prev) => ({ ...prev, amount: getNumberFromIndianCurrencyString(amount) }));
};

export const handleClawbackPeriodChange = (value: string, setFormProps: (value: React.SetStateAction<FormProps>) => void) => {

    if(Number(value) || Number(value) === 0){
        setFormProps((prev) => ({ ...prev, clawback_period: value ? parseInt(value) : null }));
    }

};

export const handlePayoutMonthChange = (year: number, monthIndex: number, setFormProps: (value: React.SetStateAction<FormProps>) => void) => {
    const month = String(monthIndex + 1).padStart(2, '0');
    setFormProps((prev) => ({ ...prev, payout_month: `${year}-${month}-01` }));
};

export const handleBooleanChange = (e: { name?: string; value?: boolean | string }, setFormProps: (value: React.SetStateAction<FormProps>) => void) => {
    const { name, value } = e;
    setFormProps((prev) => ({ ...prev, [name || '']: (value === 'true' || value === true) }));
};

export const validateFormVal = ( value : 'organization_bonus_setting_id' | 'amount' | 'clawback_period', formProps : FormProps, setValidationStates? : (value: React.SetStateAction<ValidationStates>) => void ) => {
    
    if(!formProps[value]){
        setValidationStates && setValidationStates((prev) => ({ ...prev, [value]: 'error' }));
        return false
    }
    return true

}

export const getBonusAmountForFinancialYear = (
    bonuses: BonusRow[],
    financialYearStart: number,
) =>
    bonuses
        .filter((bonus) => {
            const payoutDate = parsePayrollMonth(toPayrollMonth(bonus.payout_month));
            if (!payoutDate) {
                return false;
            }
            return getFinancialYearFromDate(payoutDate) === financialYearStart;
        })
        .reduce((total, bonus) => total + (bonus.amount ?? 0), 0);

const isRearchRow = (bonusData : BonusRow) => Boolean(bonusData.assignment_id);

export const isPayrollExecuted = (bonusData : BonusRow) =>
    isRearchRow(bonusData) &&
    bonusData.status?.toString() === bonusStatus.BONUS_STATUS_PAID &&
    bonusData.source !== bonusSource.BONUS_SOURCE_OUTSIDE_XPAYROLL;

export const canEdit = (permission : boolean, bonusData : BonusRow) => {

    if(!permission){
        return false
    }

    switch(bonusData.status?.toString()){

        case bonusStatus.BONUS_STATUS_CLAWBACK_APPLIED:
            return false;

        case bonusStatus.BONUS_STATUS_PENDING:
            return true;

        case bonusStatus.BONUS_STATUS_PAID:
            if(!isRearchRow(bonusData)){
                return true;
            }
            return !isPayrollExecuted(bonusData);

    }

}

export const formatPayoutMonth = (payoutMonth: string | null) =>
    formatPayrollMonth(toPayrollMonth(payoutMonth), 'MMM yyyy') || '-';

export const isClawbackApplied = (status: number | null) =>
    status?.toString() === bonusStatus.BONUS_STATUS_CLAWBACK_APPLIED;

export const getClawbackText = (bonus: BonusRow) => {
    if (!bonus.clawback_applicable || !bonus.clawback_period) {
        return { main: 'Not Applicable', sub: undefined };
    }

    const main = `${bonus.clawback_period} ${bonus.clawback_period > 1 ? 'Months' : 'Month'}`;

    if (isClawbackApplied(bonus.status)) {
        const raisedIn = toPayrollMonth(bonus.clawback_month);
        const label = formatPayrollMonth(raisedIn, 'MMM yyyy');
        return { main, sub: label ? `Applied ${label}` : undefined };
    }

    const windowStartMonth = toPayrollMonth(bonus.payout_month);
    const windowStartDate = parsePayrollMonth(windowStartMonth);
    const windowEndMonth = getClawbackWindowEndMonth(windowStartMonth, bonus.clawback_period);
    const windowEndDate = parsePayrollMonth(windowEndMonth);

    if (!windowStartDate || !windowEndDate) {
        return { main, sub: undefined };
    }

    if (differenceInCalendarMonths(windowStartDate, new Date()) > 0) {
        return { main, sub: `Starts ${formatPayrollMonth(windowStartMonth, 'MMM yyyy')}` };
    }

    const monthsLeft = Math.max(differenceInCalendarMonths(windowEndDate, new Date()) + 1, 0);

    return monthsLeft === 0
        ? { main, sub: `Ended ${formatPayrollMonth(windowEndMonth, 'MMM yyyy')}` }
        : { main, sub: `${monthsLeft} ${monthsLeft === 1 ? 'Month' : 'Months'} Left` };
};

export const STATUS_BADGE: Record<string, { label: string; color: BadgeProps['color'] }> = {
    [bonusStatus.BONUS_STATUS_PENDING]: { label: 'To be paid', color: 'neutral' },
    [bonusStatus.BONUS_STATUS_PAID]: { label: 'Already Paid', color: 'positive' },
    [bonusStatus.BONUS_STATUS_CLAWBACK_APPLIED]: {
        label: 'Clawback applicable',
        color: 'notice',
    },
};

export const getDeleteBonusConfirmationText = (
    bonusName: string | null,
    affectedSiblingsCount: number,
    affectedTotal: number,
    selectedBonusAmount: number | null | undefined,
    firstName: string | null,
) => {
    const scopeText =
        affectedSiblingsCount > 1
            ? ` and all ${affectedSiblingsCount} of its payout months (${toIndianCurrencyString(
                  affectedTotal,
                  false,
                  2,
              )} in total)`
            : ` of ${
                  selectedBonusAmount != null
                      ? toIndianCurrencyString(selectedBonusAmount, false, 2)
                      : 'N/A'
              }`;

    return `Bonus "${bonusName ?? ''}"${scopeText} will be cancelled and removed from ${firstName}'s profile.`;
};

export const canDelete = (permission : boolean, bonusData : BonusRow) => {

    if(!permission){
        return false
    }

    switch(bonusData.status?.toString()){

                
        case bonusStatus.BONUS_STATUS_PENDING:
            return true;

        case bonusStatus.BONUS_STATUS_PAID:
            if(!isRearchRow(bonusData)){
                return bonusData.source !== bonusSource.BONUS_SOURCE_WITHIN_XPAYROLL;
            }
            return !isPayrollExecuted(bonusData);

        case bonusStatus.BONUS_STATUS_CLAWBACK_APPLIED:
            return false;

    }

}